import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import type { FlatNode, TranscriptEntry } from "@wapadesk/engine";
import { groupTranscript, isCardNode, isOpen, renderTemplate } from "@wapadesk/engine";
import { CardInput } from "./CardInput";
import { Qr } from "./Qr";
import { WaIcon } from "./WaIcon";
import { BookingPage, type BookingPrefill } from "./BookingPage";
import { isPreview } from "../lib/preview";
import { t, resolveLocale } from "@wapadesk/i18n";
import { applyTheme } from "../lib/theme";
import { send } from "../lib/bridge";
import { useConversation, type State } from "../store";
import { InputFor } from "./inputs";
import { BookedBubble, Booking } from "./Booking";

const API_ORIGIN =
  (import.meta.env["VITE_API_ORIGIN"] as string | undefined) ?? "https://api.wapadesk.com";
const BADGE_URL =
  "https://wapadesk.com/?utm_source=widget&utm_medium=badge&utm_campaign=powered_by";

function answerLabel(
  entry: Extract<TranscriptEntry, { kind: "answer" }>,
  config: State["config"],
): string {
  const n = entry.node.node;
  const v = entry.value;
  const opt = (id: string) => {
    if (id.startsWith("other:")) return id.slice(6);
    if ((n.type === "single_choice" || n.type === "multi_choice") && n.options)
      return n.options.find((o) => o.id === id)?.label ?? id;
    return config?.procedures.find((p) => p.id === id)?.name ?? id;
  };
  if (n.type === "consent") return "✓";
  if (n.type === "file_upload")
    return Array.isArray(v) ? `${v.length} ${v.length === 1 ? "file" : "files"}` : "";
  if (Array.isArray(v)) return v.map(opt).join(", ");
  if (typeof v === "string" && (n.type === "single_choice" || n.type === "procedure_picker"))
    return opt(v);
  if (typeof v === "object" && v !== null) return "✓";
  return String(v);
}

export function App() {
  const { state, actions } = useConversation(API_ORIGIN);
  const locale = resolveLocale(state.init?.locale ?? state.config?.clinic.defaultLocale);
  const [inputError, setInputError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const reduced =
    typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") actions.close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (state.config) {
      applyTheme(document.documentElement, state.config.branding.color);
      document.documentElement.lang = locale;
    }
  }, [state.config, locale]);

  // Typing delay: reveal system bubbles one by one (skippable, off for reduced motion).
  const transcript = state.flow?.transcript ?? [];
  useEffect(() => {
    if (state.shownUpTo >= transcript.length) return;
    const delay = reduced ? 0 : Math.min(state.config?.typingDelayMs ?? 600, 1200);
    const entry = transcript[state.shownUpTo];
    const cardForm = state.config?.form?.schema;
    // Questions on a card step are never shown as bubbles (the card shows them all at once), so
    // they get no typing pause; otherwise a contact card of eight fields cost ~5 s of dots before
    // and after it (found while recording the demo, 14 October 2026).
    const hidden = Boolean(entry && cardForm && isCardNode(cardForm, entry.node));
    const id = setTimeout(() => actions.reveal(), entry?.kind === "system" && !hidden ? delay : 0);
    return () => clearTimeout(id);
  }, [state.shownUpTo, transcript.length, reduced]);

  useEffect(() => {
    listRef.current?.scrollTo({
      top: listRef.current.scrollHeight,
      behavior: reduced ? "auto" : "smooth",
    });
  }, [state.shownUpTo, state.activeNodeId, state.helpOpen, state.handoff]);

  // Mobile keyboards shrink the visual viewport: size the shell to it and keep the question in view.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const apply = () => {
      document.documentElement.style.setProperty("--vvh", `${Math.round(vv.height)}px`);
      listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "auto" });
    };
    apply();
    vv.addEventListener("resize", apply);
    return () => vv.removeEventListener("resize", apply);
  }, []);

  useEffect(() => {
    if (state.activeNodeId && state.shownUpTo >= transcript.length) {
      const f = state.flow?.transcript.find((e) => e.node.node.id === state.activeNodeId);
      if (f)
        actions.track("node_shown", {
          nodeId: f.node.node.id,
          stepId: f.node.stepId,
          nodeType: f.node.node.type,
        });
      if (f?.node.node.type === "consent") actions.track("consent_shown");
    }
  }, [state.activeNodeId, state.shownUpTo >= transcript.length]);

  const active: FlatNode | undefined = useMemo(() => {
    if (!state.flow) return undefined;
    if (state.editingNodeId)
      return state.flow.transcript.find((e) => e.node.node.id === state.editingNodeId)?.node;
    return state.flow.next;
  }, [state.flow, state.editingNodeId]);

  const config = state.config;
  const clinicName = config?.clinic.name ?? "";
  const ctx = { clinic: { name: clinicName }, answers: state.answers };
  const revealed = state.shownUpTo >= transcript.length;

  const form = config?.form?.schema;
  const onCardSubmit = async (values: Array<{ node: FlatNode; value: unknown }>) => {
    setBusy(true);
    setInputError(undefined);
    for (const { node, value } of values) {
      // Optional fields left empty are saved as "" so a resume does not re-ask them (flow.ts rule).
      const r = await actions.answer(node, value);
      if (!r.ok) {
        setInputError(r.code);
        break;
      }
    }
    if (state.editingNodeId) actions.edit(undefined);
    setBusy(false);
  };
  const onSubmit = async (raw: unknown) => {
    if (!active) return;
    setBusy(true);
    setInputError(undefined);
    const r = await actions.answer(active, raw);
    setBusy(false);
    if (!r.ok) setInputError(r.code);
  };

  const [choosing, setChoosing] = useState(false);
  const agentCfg = state.config?.widget?.agents;
  const onlineAgents = (agentCfg?.list ?? []).filter((a) => a.online);
  const onWhatsApp = async (agentId?: string) => {
    // Section 14b "choose": the patient picks the person before WhatsApp opens.
    if (!agentId && agentCfg?.routing === "choose" && onlineAgents.length > 0) {
      setChoosing(true);
      return;
    }
    setChoosing(false);
    const h = await actions.handoff("whatsapp", active?.node.id, agentId);
    if (!h) return;
    // Live funnel preview: show the message instead of opening WhatsApp.
    if (preview) {
      setPreviewMsg(h.message ?? "");
      return;
    }
    // A real anchor click keeps the user gesture so mobile browsers hand off to the app.
    const a = document.createElement("a");
    a.href = h.url;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => {
      // Fallback for browsers that block anchors inside iframes.
      send({ source: "wapadesk", type: "navigate", payload: { url: h.url, target: "_blank" } });
    }, 400);
  };
  const preview = isPreview();
  const [previewMsg, setPreviewMsg] = useState<string | undefined>();
  const [emailState, setEmailState] = useState<"idle" | "sending" | "sent" | "failed">("idle");
  const [phoneQr, setPhoneQr] = useState<string | "loading" | "error" | undefined>();
  const onPhoneQr = async () => {
    setPhoneQr("loading");
    const token = await actions.handoffLink(active?.node.id);
    setPhoneQr(token ? `${API_ORIGIN}/wa/${token}` : "error");
  };
  const onEmail = async () => {
    if (emailState === "sending" || emailState === "sent") return;
    setEmailState("sending");
    const h = await actions.handoff("email", active?.node.id);
    setEmailState(h ? "sent" : "failed");
    if (preview && h) setPreviewMsg(h.message ?? "");
  };
  const [bookingView, setBookingView] = useState(false);
  const onBooking = async () => {
    // Internal booking: the booking page (15 October 2026), a classic form over the chat.
    if (config?.booking.type === "internal") {
      setBookingView(true);
      return;
    }
    const h = await actions.handoff("booking", active?.node.id);
    if (!h) return;
    send({ source: "wapadesk", type: "navigate", payload: { url: h.url, target: "_blank" } });
  };

  if (state.phase === "loading")
    return (
      <main class="shell center" aria-busy="true">
        <div class="spinner" aria-label="Loading" />
      </main>
    );

  // The person the visitor will talk to: an online agent with a photo, else any agent with one.
  const agentList = config?.widget?.agents?.list ?? [];
  const host: Host | undefined =
    agentList.find((a) => a.online && a.photoUrl) ?? agentList.find((a) => a.photoUrl);
  // Inline embed (data-wapadesk-inline): no close button, the chat lives in the page.
  const standalone = Boolean(state.init?.standalone);
  const inline =
    standalone ||
    (typeof location !== "undefined" && new URLSearchParams(location.search).get("inline") === "1");
  if (state.phase === "fallback" || state.phase === "error") {
    const number = config?.whatsappNumber?.replace(/\D/g, "");
    const url = number
      ? `https://wa.me/${number}?text=${encodeURIComponent(`Hello ${clinicName}, I'm contacting you from your website.`)}`
      : undefined;
    const expired = state.error === "session_expired";
    return (
      <main class={`shell${standalone ? " standalone" : ""}`}>
        <Header
          name={clinicName}
          locale={locale}
          onClose={inline ? undefined : actions.close}
          host={host}
          logoUrl={config?.clinic.logoUrl}
        />
        <div class="center col">
          <h2>
            {state.phase === "error"
              ? t(locale, "chrome.errorTitle")
              : t(locale, "chrome.fallbackTitle")}
          </h2>
          <p class="muted">
            {expired
              ? t(locale, "chrome.sessionExpired")
              : state.phase === "error"
                ? t(locale, "chrome.errorBody")
                : t(locale, "chrome.fallbackBody")}
          </p>
          {url && (
            <a class="primary wa" href={url} target="_blank" rel="noopener">
              <WaIcon /> {t(locale, "chrome.continueOnWhatsApp")}
            </a>
          )}
        </div>
        {config?.branding.showBadge !== false && (
          <Badge
            slug={config?.clinic.slug}
            locale={locale}
            onClick={() => actions.track("badge_click")}
          />
        )}
      </main>
    );
  }

  const bookingOn =
    config?.booking.type === "external" ||
    (config?.booking.type === "internal" && !state.booking?.booked);
  const bookingOpen = Boolean(state.booking?.open);
  // Procedures the patient already named in the funnel, pre-ticked on the booking's procedure step.
  const suggestedProcedures = (() => {
    const names: string[] = [];
    for (const st of form?.steps ?? [])
      for (const n of st.nodes) {
        if (n.type !== "procedure_picker") continue;
        const v = state.answers[n.id];
        const ids = Array.isArray(v) ? v : typeof v === "string" ? [v] : [];
        for (const id of ids) {
          const name = config?.procedures.find((p) => p.id === id)?.name;
          if (name && !names.includes(name)) names.push(name);
        }
      }
    return names;
  })();
  // Booking-first (appointment page embed or /go/<slug>?start=booking): once consent and the
  // first step (contact details) are done, open the appointment picker without being asked.
  const bookingFirst =
    typeof location !== "undefined" &&
    new URLSearchParams(location.search).get("start") === "booking" &&
    config?.booking.type === "internal";
  const [autoOpened, setAutoOpened] = useState(false);
  useEffect(() => {
    if (!bookingFirst || autoOpened || !state.consented) return;
    // Consent lives in the first step; the contact details are the step after it.
    const consentStep =
      form?.steps.findIndex((st) => st.nodes.some((n) => n.type === "consent")) ?? 0;
    if ((state.flow?.completedSteps.length ?? 0) < Math.max(consentStep, 0) + 2) return;
    if (state.booking?.booked || state.booking?.open) return;
    setAutoOpened(true);
    void actions.openBooking(active?.node.id);
  }, [bookingFirst, autoOpened, state.consented, state.flow?.completedSteps.length]);
  const showQuick = state.consented;
  const away = Boolean(config?.widget && !isOpen(config.widget.hours));
  // "Continue on your phone" (14b): offered on a desktop host page, decided by the loader from
  // the page's own width (the chat window itself is only about 440px wide there).
  const phoneOffer =
    Boolean(config?.widget?.qrOnDesktop) &&
    state.consented &&
    state.init?.context.device === "desktop";

  if (bookingView && config)
    return (
      <div class={`shell${standalone ? " standalone" : ""}`} style="position:relative">
        <div class="bp-overlay">
          <BookingPage
            clinicRef={config.clinicId}
            apiOrigin={API_ORIGIN}
            sessionToken={actions.sessionToken()}
            prefill={prefillFrom(config, state.answers)}
            consented={state.consented}
            onClose={() => setBookingView(false)}
            onBooked={(s) => actions.bookedFromPage(s)}
          />
        </div>
      </div>
    );

  return (
    <main class={`shell${standalone ? " standalone" : ""}`}>
      <Header
        name={clinicName}
        locale={locale}
        onClose={inline ? undefined : actions.close}
        host={host}
        logoUrl={config?.clinic.logoUrl}
      />
      <div class="list" ref={listRef}>
        <div class="bubbles" role="log" aria-live="polite" aria-relevant="additions">
          {groupTranscript(form!, transcript.slice(0, state.shownUpTo)).map((g, i) => {
            if (g.kind === "card") {
              const editable = !state.whatsappOpened;
              const summary = g.entries
                .map((e) => `${e.node.node.prompt ?? e.node.node.id}: ${answerLabel(e, config)}`)
                .join("\n");
              return (
                <div key={`c-${g.stepId}-${i}`}>
                  <div class="row-s">
                    <div class="bubble sys">{g.stepTitle}</div>
                  </div>
                  {g.entries.length > 0 && (
                    <div class="row-a">
                      {editable ? (
                        <button
                          type="button"
                          class="bubble me card"
                          aria-label={`${t(locale, "chrome.edit")}: ${g.stepTitle}`}
                          onClick={() => actions.edit(g.entries[0]!.node.node.id)}
                        >
                          {summary}
                        </button>
                      ) : (
                        <div class="bubble me card">{summary}</div>
                      )}
                    </div>
                  )}
                </div>
              );
            }
            const e = g.entry;
            const n = e.node.node;
            if (e.kind === "system") {
              const text = n.type === "message" ? n.text : (n.prompt ?? "");
              if (!text.trim() && !n.help) return null;
              return (
                <div key={`s-${n.id}-${i}`} class="row-s">
                  <div class="bubble sys">
                    {n.type === "message" && n.imagePath && /^https:\/\//.test(n.imagePath) && (
                      <img class="pic" src={n.imagePath} alt="" loading="lazy" />
                    )}
                    {renderTemplate(text, ctx)}
                    {n.help && (
                      <button
                        type="button"
                        class="help"
                        aria-label={t(locale, "chrome.help")}
                        aria-expanded={state.helpOpen === n.id}
                        onClick={() => actions.help(state.helpOpen === n.id ? undefined : n.id)}
                      >
                        ?
                      </button>
                    )}
                  </div>
                  {state.helpOpen === n.id && n.help && (
                    <div class="bubble info">{renderTemplate(n.help, ctx)}</div>
                  )}
                </div>
              );
            }
            const editable = n.type !== "consent" && !state.whatsappOpened;
            return (
              <div key={`a-${n.id}`} class="row-a">
                {editable ? (
                  <button
                    type="button"
                    class="bubble me"
                    aria-label={`${t(locale, "chrome.edit")}: ${answerLabel(e, config)}`}
                    onClick={() => actions.edit(n.id)}
                  >
                    {answerLabel(e, config)}
                  </button>
                ) : (
                  <div class="bubble me">{answerLabel(e, config)}</div>
                )}
              </div>
            );
          })}
          {!revealed && (
            <div class="bubble sys typing" aria-hidden="true">
              <span />
              <span />
              <span />
            </div>
          )}
          {revealed && state.flow?.completed && !state.editingNodeId && (
            <div class="row-s">
              <div class="bubble sys">{t(locale, "chrome.completed")}</div>
            </div>
          )}
          {state.booking?.booked && (
            <div class="row-s">
              <div class="bubble info">
                <BookedBubble locale={locale} booked={state.booking.booked} />
              </div>
            </div>
          )}
          {state.booking?.error === "unavailable" && !state.booking.open && (
            <div class="row-s">
              <div class="bubble info">{t(locale, "booking.unavailable")}</div>
            </div>
          )}
          {state.handoff?.type === "whatsapp" && !preview && (
            <div class="row-s">
              <div class="bubble info">{t(locale, "chrome.whatsappHint")}</div>
            </div>
          )}
          {preview && previewMsg !== undefined && (
            <div class="row-s">
              <div class="bubble info preview-msg">
                <strong>{t(locale, "chrome.previewMessage")}</strong>
                <pre>{previewMsg}</pre>
              </div>
            </div>
          )}
        </div>
      </div>
      <div class="dock">
        {bookingOpen && state.booking && (
          <Booking
            locale={locale}
            booking={state.booking}
            actions={actions}
            atNodeId={active?.node.id}
            suggested={suggestedProcedures}
          />
        )}
        {!bookingOpen && revealed && active && form && isCardNode(form, active) && (
          <CardInput
            key={`card-${active.stepId}-${state.editingNodeId ?? ""}`}
            form={form}
            stepId={active.stepId}
            stepTitle={active.stepTitle}
            locale={locale}
            answers={state.answers}
            busy={busy}
            onSubmit={onCardSubmit}
          />
        )}
        {!bookingOpen && revealed && active && !(form && isCardNode(form, active)) && (
          <InputFor
            key={active.node.id + (state.editingNodeId ? "-edit" : "")}
            node={active}
            locale={locale}
            clinicName={clinicName}
            config={config!}
            initial={state.answers[active.node.id]}
            files={state.files}
            error={inputError}
            busy={busy}
            onSubmit={onSubmit}
            onSkip={
              !active.node.required && active.node.type !== "consent"
                ? () => actions.skip(active)
                : undefined
            }
            onDecline={active.node.type === "consent" ? actions.declineConsent : undefined}
            onUpload={(f) => actions.upload(active, f)}
          />
        )}
        {!bookingOpen && revealed && state.flow?.completed && !state.editingNodeId && (
          // Owner decision (11 October 2026): the end is a handoff, not a choice. Booking was offered along the way.
          <div class="in end">
            <button type="button" class="primary wa" onClick={() => void onWhatsApp()}>
              <WaIcon /> {t(locale, "chrome.openWhatsApp")}
            </button>
            {config?.clinic.contactEmail && emailState !== "sent" && (
              <button
                type="button"
                class="link-btn"
                disabled={emailState === "sending"}
                onClick={() => void onEmail()}
              >
                {emailState === "sending"
                  ? t(locale, "chrome.sendingEmail")
                  : t(locale, "chrome.sendByEmail")}
              </button>
            )}
            {emailState === "sent" && (
              <p class="email-sent" role="status">
                {t(locale, "chrome.emailSent")}
              </p>
            )}
            {emailState === "failed" && (
              <p class="email-sent" role="alert">
                {t(locale, "chrome.emailFailed")}
              </p>
            )}
            {phoneOffer && phoneQr === undefined && (
              <button type="button" class="link-btn" onClick={() => void onPhoneQr()}>
                {t(locale, "chrome.phoneOffer")}
              </button>
            )}
            {phoneQr === "loading" && <p class="email-sent">{t(locale, "chrome.phoneLoading")}</p>}
            {phoneQr === "error" && (
              <p class="email-sent" role="alert">
                {t(locale, "chrome.phoneFailed")}
              </p>
            )}
            {phoneQr && phoneQr !== "loading" && phoneQr !== "error" && (
              <Qr text={phoneQr} locale={locale} hint={t(locale, "chrome.phoneHint")} />
            )}
          </div>
        )}
        {away && (
          <p class="away" role="status">
            {config?.widget?.hours.awayMessage}
          </p>
        )}
        {choosing && (
          <div class="agents" role="group" aria-label={t(locale, "chrome.chooseAgent")}>
            <span class="small">{t(locale, "chrome.chooseAgent")}</span>
            {onlineAgents.map((a) => (
              <button key={a.id} type="button" class="qa" onClick={() => void onWhatsApp(a.id)}>
                <WaIcon small /> {a.name}
                {a.title ? <span class="muted"> · {a.title}</span> : null}
              </button>
            ))}
          </div>
        )}
        {showQuick &&
          !(state.flow?.completed && !state.editingNodeId) &&
          !choosing &&
          !bookingOpen && (
            <div class="quick">
              <button type="button" class="qa" onClick={() => void onWhatsApp()}>
                <WaIcon small /> {t(locale, "chrome.skipToWhatsApp")}
              </button>
              {bookingOn && (
                <button type="button" class="qa" onClick={onBooking}>
                  {config?.booking.externalLabel
                    ? `${t(locale, "chrome.orBook").split(" ")[0]} ${config.booking.externalLabel}`
                    : t(locale, "chrome.orBook")}
                </button>
              )}
            </div>
          )}
        <div class="status" aria-live="polite">
          {state.saving > 0 ? t(locale, "chrome.saving") : ""}
        </div>
      </div>
      {config?.branding.showBadge !== false && (
        <Badge
          slug={config?.clinic.slug}
          locale={locale}
          onClick={() => actions.track("badge_click")}
        />
      )}
    </main>
  );
}

type Host = { name: string; title?: string | undefined; photoUrl?: string | undefined };

function Header({
  name,
  locale,
  onClose,
  host,
  logoUrl,
}: {
  name: string;
  locale: string;
  onClose?: () => void;
  host?: Host | undefined;
  logoUrl?: string | undefined;
}) {
  const photo = host?.photoUrl ?? logoUrl;
  return (
    <header class="head">
      <div class="avatar" aria-hidden="true">
        {photo ? (
          <img src={photo} alt="" />
        ) : (
          <svg viewBox="0 0 40 40" class="person">
            <circle cx="20" cy="15" r="7" />
            <path d="M8 34c1.5-7 6-10 12-10s10.5 3 12 10" />
          </svg>
        )}
      </div>
      <div class="titles">
        <div class="name">{name}</div>
        <div class="sub">
          {host
            ? `${host.name}${host.title ? ` · ${host.title}` : ""}`
            : t(locale, "chrome.subtitle")}
        </div>
      </div>
      {onClose && (
        <button
          type="button"
          class="close"
          aria-label={t(locale, "chrome.close")}
          onClick={onClose}
        >
          ×
        </button>
      )}
    </header>
  );
}

function Badge({ slug, locale, onClick }: { slug?: string; locale: string; onClick: () => void }) {
  const href = `${BADGE_URL}${slug ? `&ref=${encodeURIComponent(slug)}` : ""}`;
  return (
    <footer class="badge">
      <a
        href={href}
        target="_blank"
        rel="noopener"
        aria-label={t(locale, "chrome.poweredByA11y")}
        onClick={onClick}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" width="12" height="12">
          <path d="M4 5h16v10H9l-5 4z" fill="currentColor" />
        </svg>
        {t(locale, "chrome.poweredBy")}
      </a>
    </footer>
  );
}

/** The patient's name, email and phone from the chat answers, for the booking page. */
function prefillFrom(
  config: NonNullable<State["config"]>,
  answers: Record<string, unknown>,
): BookingPrefill {
  const out: BookingPrefill = {};
  const schema = config.form?.schema;
  if (!schema) return out;
  for (const step of schema.steps)
    for (const n of step.nodes) {
      const v = answers[n.id];
      if (typeof v !== "string" || !v.trim() || !("maps" in n) || !n.maps) continue;
      if (n.maps === "contact.firstName") out.firstName = v.trim();
      else if (n.maps === "contact.lastName") out.lastName = v.trim();
      else if (n.maps === "contact.email") out.email = v.trim();
      else if (n.maps === "contact.phone") out.phone = v.trim();
      else if (n.maps === "contact.fullName") {
        const [first, ...rest] = v.trim().split(/\s+/);
        out.firstName ??= first;
        if (rest.length) out.lastName ??= rest.join(" ");
      }
    }
  return out;
}
